import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  TransientTimeTab,
  TransientColumnGroupId,
  TransientGroupingOption,
  TransientStatus,
  SpaceType,
} from '@/types/transient-stays'
import { getDefaultColumnGroups, getDefaultQuickFilters, TRANSIENT_COLUMN_GROUPS } from '@/types/transient-stays'

// ============================================
// Types
// ============================================

export type DetailPanelMode = 'modal' | 'sidePanel'

interface TransientViewState {
  activeTab: TransientTimeTab
  quickFilters: { status: TransientStatus[]; spaceType: SpaceType[]; locationIds: string[] }
  groupBy: TransientGroupingOption
  visibleColumnGroups: Set<TransientColumnGroupId>
  search: string
  detailPanelMode: DetailPanelMode
  selectedStayId: string | null
}

interface TransientViewActions {
  setActiveTab: (tab: TransientTimeTab) => void
  setStatusFilter: (values: TransientStatus[]) => void
  setSpaceTypeFilter: (values: SpaceType[]) => void
  setLocationFilter: (values: string[]) => void
  resetFiltersToTabDefaults: () => void
  getIsModified: () => boolean
  setGroupBy: (option: TransientGroupingOption) => void
  toggleColumnGroup: (groupId: TransientColumnGroupId) => void
  setSearch: (search: string) => void
  isColumnGroupVisible: (groupId: TransientColumnGroupId) => boolean
  setDetailPanelMode: (mode: DetailPanelMode) => void
  setSelectedStayId: (id: string | null) => void
}

// ============================================
// Helpers
// ============================================

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((v, i) => v === sortedB[i])
}

// ============================================
// Store
// ============================================

export const useTransientViewStore = create<TransientViewState & TransientViewActions>()(
  devtools(
    persist(
      (set, get) => ({
        activeTab: 'today',
        quickFilters: getDefaultQuickFilters('today'),
        groupBy: 'none',
        visibleColumnGroups: new Set(getDefaultColumnGroups('today')),
        search: '',
        detailPanelMode: 'sidePanel',
        selectedStayId: null,

        setActiveTab: (tab) => {
          set({
            activeTab: tab,
            quickFilters: getDefaultQuickFilters(tab),
            groupBy: 'none',
            visibleColumnGroups: new Set(getDefaultColumnGroups(tab)),
            search: '',
            selectedStayId: null,
          })
        },

        setStatusFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, status: values },
          })),

        setSpaceTypeFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, spaceType: values },
          })),

        setLocationFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, locationIds: values },
          })),

        resetFiltersToTabDefaults: () => {
          const { activeTab } = get()
          set({
            quickFilters: getDefaultQuickFilters(activeTab),
            groupBy: 'none',
          })
        },

        getIsModified: () => {
          const { activeTab, quickFilters } = get()
          const defaults = getDefaultQuickFilters(activeTab)
          return (
            !arraysEqual(quickFilters.status, defaults.status) ||
            !arraysEqual(quickFilters.spaceType, defaults.spaceType) ||
            !arraysEqual(quickFilters.locationIds, defaults.locationIds)
          )
        },

        setGroupBy: (option) => set({ groupBy: option }),

        toggleColumnGroup: (groupId) =>
          set((state) => {
            const group = TRANSIENT_COLUMN_GROUPS.find((g) => g.id === groupId)
            if (group?.alwaysOn) return state

            const next = new Set(state.visibleColumnGroups)
            if (next.has(groupId)) {
              next.delete(groupId)
            } else {
              next.add(groupId)
            }
            return { visibleColumnGroups: next }
          }),

        setSearch: (search) => set({ search }),

        isColumnGroupVisible: (groupId) => {
          const group = TRANSIENT_COLUMN_GROUPS.find((g) => g.id === groupId)
          if (group?.alwaysOn) return true
          return get().visibleColumnGroups.has(groupId)
        },

        setDetailPanelMode: (mode) => set({ detailPanelMode: mode }),

        setSelectedStayId: (id) => set({ selectedStayId: id }),
      }),
      {
        name: 'transient-view',
        partialize: (state) => ({
          activeTab: state.activeTab,
          groupBy: state.groupBy,
          visibleColumnGroups: Array.from(state.visibleColumnGroups),
          detailPanelMode: state.detailPanelMode,
        }),
        merge: (persisted, current) => {
          const p = persisted as {
            activeTab?: TransientTimeTab
            groupBy?: TransientGroupingOption
            visibleColumnGroups?: TransientColumnGroupId[]
            detailPanelMode?: DetailPanelMode
          }

          const validTabs: TransientTimeTab[] = ['today', 'upcoming', 'checked_out', 'all']
          const activeTab = p.activeTab && validTabs.includes(p.activeTab)
            ? p.activeTab
            : current.activeTab

          return {
            ...current,
            activeTab,
            groupBy: p.groupBy ?? current.groupBy,
            visibleColumnGroups: p.visibleColumnGroups
              ? new Set(p.visibleColumnGroups)
              : current.visibleColumnGroups,
            detailPanelMode: p.detailPanelMode ?? current.detailPanelMode,
            quickFilters: getDefaultQuickFilters(activeTab),
          }
        },
      }
    ),
    { name: 'transient-view' }
  )
)
